/**
 * Authentication Service
 * Handles token management, login/logout, and debug mode
 */

const DEBUG_MODE = process.env.REACT_APP_DEBUG_MODE === 'true';
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

const getBaseURL = () => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL.replace('/api', '');
  }
  return window.location.origin;
};

/**
 * Check if user is authenticated
 * @returns {boolean} true if authenticated
 */
export const isAuthenticated = () => {
  if (DEBUG_MODE) {
    return true;
  }
  return getToken() !== null;
};

/**
 * Get stored token
 * @returns {string|null} JWT token or null
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Save token to localStorage
 * @param {string} token JWT token
 */
export const saveToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  }
};

/**
 * Remove token from localStorage
 */
export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

/**
 * Save user info
 * @param {object} user User object
 */
export const saveUser = (user) => {
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
};

/**
 * Get stored user info
 * @returns {object|null} User object or null
 */
export const getUser = () => {
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
};

/**
 * Login function
 * @param {string} loginId Login ID
 * @param {string} password Password
 * @returns {Promise<object>} Login response with token
 */
export const login = async (loginId, password) => {
  if (DEBUG_MODE) {
    // In debug mode, skip actual login and return mock response
    const mockUser = {
      loginId: 'debug-user',
      role: 'DEBUG_USER',
      token: 'debug-token'
    };
    saveUser(mockUser);
    return mockUser;
  }

  try {
    const baseURL = getBaseURL();
    const response = await fetch(`${baseURL}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        loginId,
        password
      })
    });

    if (!response.ok) {
      throw new Error('Login failed');
    }

    const data = await response.json();

    // Save token and user info
    saveToken(data.token);
    saveUser({
      loginId: data.loginId,
      role: data.role,
      expiresAt: data.expiresAt
    });

    return data;
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

/**
 * Logout function
 */
export const logout = () => {
  removeToken();
};

/**
 * Handle token renewal from response header
 * Used for Sliding Window pattern
 * @param {Response} response Fetch response object
 */
export const handleTokenRenewal = (response) => {
  const newToken = response.headers.get('x-new-token');
  if (newToken) {
    saveToken(newToken);
    console.log('Token renewed via Sliding Window');
  }
};
